/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import { Box, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { useForm } from "../../../provider/Form/FormProvider";
import { getCaseLink } from "../../../services/productService";
import TextContainer from "../../Container/TextContainer";
import InsuranceData from "../Settlement/Summary/InsuranceData";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import EmailVerificationModal from "../../FormInfoModal/EmailVerificationModal";
import summaryStyle from "../ContractLaw/Summary/summaryStyle";
import SummaryDetailsItem from "../Common/SummaryDetailsItem/SummaryDetailsItem";
import SummaryDetailsPersonItems from "../Common/SummaryDetailsItem/SummaryDetailsPersonItems";
import ContractualPartnerData from "../ContractLaw/Summary/ContractualPartnerData";

type SummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

const Summary = ({ product, submitProps }: SummaryProps) => {
  const location = useLocation();
  const stateField = location.state?.field;
  const { handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  const basePath = getCaseLink(product) + "/antrag";
  const generalCivilLawBasePath = basePath + "/anliegen";
  const generalCivilLawIncidentPath = basePath + "/informationen";
  const opposingPartyPath = basePath + "/gegenseite";
  const concernSummaryPath = basePath + "/zusammenfassung";
  const insurancePath = basePath + "/versicherung";

  return (
    <Paper elevation={4}>
      <TextContainer>
        <Typography variant={"h4"} sx={summaryStyle.headline}>
          Ihre Angaben
        </Typography>
        <Grid container>
          <Box id={"generalCivilLawBase"} sx={summaryStyle.scrollToAnchor} />
          <Grid item xs={12} sx={summaryStyle.subHeadline}>
            <Typography variant={"h5"}>Ihr Anliegen</Typography>
          </Grid>
          <SummaryDetailsPersonItems basePath={"customer.person"} formUrl={generalCivilLawBasePath} caseData={product} />

          <SummaryDetailsItem fieldPath={"customer.telephone"} formUrl={generalCivilLawBasePath} caseData={product} />

          {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && !product.clientWasMinorAtContractDate && (
            <>
              <SummaryDetailsPersonItems basePath={"client"} formUrl={generalCivilLawBasePath} caseData={product} />
            </>
          )}
          {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && product.clientWasMinorAtContractDate && (
            <>
              <Grid item xs={12} sx={{ marginTop: "2rem", borderBottom: "1px solid #333", "& h5": { lineHeight: "1rem" } }}>
                <Typography variant={"h5"}>Angaben minderjährige Person</Typography>
              </Grid>

              <SummaryDetailsPersonItems basePath={"client"} formUrl={generalCivilLawBasePath} caseData={product} />

              <SummaryDetailsPersonItems basePath={"clientLegalRepresentative"} formUrl={generalCivilLawBasePath} caseData={product} />

              {product.clientLegalRepresentativeAmount === 2 && (
                <>
                  <SummaryDetailsPersonItems basePath={"secondClientLegalRepresentative"} formUrl={generalCivilLawBasePath} caseData={product} />
                  <SummaryDetailsItem fieldPath={"clientLegalRepresentativesAuthorizationType"} formUrl={generalCivilLawBasePath} caseData={product} />
                </>
              )}
            </>
          )}

          {product.caseCategory !== "advisory" && (
            <>
              <Box id={"generalCivilLawIncident"} sx={summaryStyle.scrollToAnchor} />
              <Grid item xs={12} sx={summaryStyle.subHeadline}>
                <Typography variant={"h5"}>Informationen zum Anliegen</Typography>
              </Grid>
              <SummaryDetailsItem fieldPath={"objectivesClient"} formUrl={generalCivilLawBasePath} caseData={product} fieldType={"multipleOptions"} />
              <SummaryDetailsItem fieldPath={"clientHasAlreadyClaimed"} formUrl={generalCivilLawBasePath} caseData={product} />

              {product.clientHasAlreadyClaimed === true && (
                <>
                  <SummaryDetailsItem fieldPath={"claimedDateClient"} formUrl={generalCivilLawBasePath} caseData={product} fieldType={"date"} />
                </>
              )}

              <SummaryDetailsItem fieldPath={"isDamageQuantifiable"} formUrl={generalCivilLawBasePath} caseData={product} />

              {product.isDamageQuantifiable === true && (
                <>
                  <SummaryDetailsItem fieldPath={"claimedAmountClient"} formUrl={generalCivilLawBasePath} caseData={product} fieldType={"money"} />
                  <SummaryDetailsItem fieldPath={"clientBankData.iban"} formUrl={generalCivilLawBasePath} caseData={product} />
                </>
              )}

              <SummaryDetailsItem fieldPath={"incidentDate"} formUrl={generalCivilLawIncidentPath} caseData={product} fieldType={"date"} />
              {product.objectivesClient.indexOf("injunction") > -1 && (
                <SummaryDetailsItem fieldPath={"incidentFrequency"} formUrl={generalCivilLawIncidentPath} caseData={product} />
              )}
              <SummaryDetailsItem fieldPath={"incidentLocalityDescription"} formUrl={generalCivilLawIncidentPath} caseData={product} />
              <SummaryDetailsItem fieldPath={"incidentAddress.streetAddress"} formUrl={generalCivilLawIncidentPath} caseData={product} />
              <SummaryDetailsItem fieldPath={"incidentAddress.postalCode"} formUrl={generalCivilLawIncidentPath} caseData={product} />
              <SummaryDetailsItem fieldPath={"incidentAddress.addressLocality"} formUrl={generalCivilLawIncidentPath} caseData={product} />
              <SummaryDetailsItem fieldPath={"policeInvolved"} formUrl={generalCivilLawIncidentPath} caseData={product} fieldType={"yesNo"} />
              {product.policeInvolved === true && (
                <>
                  <SummaryDetailsItem fieldPath={"policeReportReferenceNumber"} formUrl={generalCivilLawIncidentPath} caseData={product} />
                  <SummaryDetailsItem fieldPath={"policeDepartmentName"} formUrl={generalCivilLawIncidentPath} caseData={product} />
                </>
              )}
            </>
          )}

          {product.caseCategory !== "advisory" && (
            <>
              <Box id={"contractualPartnerData"} sx={summaryStyle.scrollToAnchor} />
              <ContractualPartnerData title={"Informationen zur Gegenseite"} formPath={opposingPartyPath} formData={product} />
            </>
          )}

          <Box id={"concernSummary"} sx={summaryStyle.scrollToAnchor} />
          <Grid item xs={12} sx={summaryStyle.subHeadline}>
            <Typography variant={"h5"}>Zusammenfassung Ihres Anliegens</Typography>
          </Grid>
          <SummaryDetailsItem fieldPath={"finalClientDescriptionAndObjectives"} formUrl={concernSummaryPath} caseData={product} />

          <Box id={"insuranceData"} sx={summaryStyle.scrollToAnchor} />
          <InsuranceData title={"Versicherung"} formPath={insurancePath} product={product} />
        </Grid>
        <form onSubmit={handleSubmit}>
          <FormSubmit {...submitProps} hideBackButton={false} backLink={basePath + "/anliegen"} backText={"Alles bearbeiten"} nextText={"Angaben speichern"} />
        </form>
      </TextContainer>
      <EmailVerificationModal />
    </Paper>
  );
};

export default Summary;
